'use client';

import Box from '@mui/material/Box';
import { animated, to, useInView, useSpring, type SpringValue } from 'react-spring';
import { type ReactNode } from 'react';
import { djup, hexTillRgba, ljus, type Kombonamn } from './rörelse-konfig';
import type { IFärgKombo } from 'src/theme/NyttFärgtema';

const AnimeradDiv = animated('div');

export type Däckkort = { id: string; framsida: ReactNode; baksida?: ReactNode };

type Props = {
    kort: Däckkort[];
    /**
     * Scroll-bundet framsteg. Utelämnas för en scen högst upp på sidan: där startar
     * framsteget redan på ~0,5 (scenen fyller vyporten från början) och hinner aldrig
     * svepa 0 → 1 medan läsaren tittar. Utan värde driver däcket sin egen entré i stället.
     */
    framsteg?: SpringValue<number>;
    läge?: 'solfjäder' | 'stapel' | 'ström' | 'vänd';
    kombo: Kombonamn | IFärgKombo;
    höjd?: number;
    etikett: string;
};

/**
 * N kort vars läge härleds ur ett enda framstegsvärde.
 *
 * Ingenting renderas om: värdet fanar ut till N interpolationer med `.to()`, och varje
 * kort får sitt eget offset. Det är skillnaden mellan en scroll-scen som kostar noll
 * React-arbete och en som kostar N omrenderingar per bildruta.
 */
export function Kortdäck({ kort, framsteg, läge = 'solfjäder', kombo, höjd = 300, etikett }: Props) {
    const antal = kort.length;
    const [ref, synlig] = useInView({ once: true, amount: 0.35 });
    const { entré } = useSpring({
        entré: synlig ? 1 : 0,
        config: { tension: 150, friction: 26 }
    });
    const drivning = framsteg ?? entré;

    return (
        <Box
            ref={ref}
            role="list"
            aria-label={etikett}
            sx={{ position: 'relative', height: höjd, perspective: läge === 'vänd' ? 1200 : undefined }}
        >
            {kort.map((k, i) => {
                const andel = antal <= 1 ? 0 : i / (antal - 1);
                const eget = drivning.to((p) => Math.min(1, Math.max(0, p * 1.6 - andel * 0.5)));

                const transform = (() => {
                    switch (läge) {
                        case 'solfjäder':
                            return to([eget], (e) => {
                                const vinkel = (andel - 0.5) * 34 * e;
                                const x = (andel - 0.5) * 300 * e;
                                return `translate(-50%, 0) translateX(${x}px) rotate(${vinkel}deg)`;
                            });
                        case 'stapel':
                            return to([eget], (e) => `translate(-50%, ${(1 - e) * 40 + i * 10}px) scale(${0.94 + e * 0.06})`);
                        case 'ström':
                            return to([eget], (e) => `translate(-50%, 0) translateX(${(1 - e) * 340 - 40}px)`);
                        case 'vänd':
                        default:
                            return to([eget], (e) => {
                                const x = (andel - 0.5) * 340;
                                return `translate(-50%, 0) translateX(${x}px) rotateY(${e * 180}deg)`;
                            });
                    }
                })();

                return (
                    <AnimeradDiv
                        key={k.id}
                        role="listitem"
                        style={{
                            position: 'absolute',
                            left: '50%',
                            top: 0,
                            opacity: eget.to((e) => 0.25 + e * 0.75),
                            transform,
                            transformStyle: läge === 'vänd' ? 'preserve-3d' : undefined,
                            willChange: 'transform, opacity'
                        }}
                    >
                        <Kortyta kombo={kombo} vänder={läge === 'vänd'}>
                            {k.framsida}
                        </Kortyta>
                        {läge === 'vänd' && k.baksida ? (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    transform: 'rotateY(180deg)',
                                    backfaceVisibility: 'hidden'
                                }}
                            >
                                <Kortyta kombo={kombo} vänder>
                                    {k.baksida}
                                </Kortyta>
                            </Box>
                        ) : null}
                    </AnimeradDiv>
                );
            })}
        </Box>
    );
}

function Kortyta({
    kombo,
    vänder,
    children
}: {
    kombo: Kombonamn | IFärgKombo;
    vänder?: boolean;
    children: ReactNode;
}) {
    return (
        <Box
            sx={{
                width: { xs: 210, sm: 240 },
                minHeight: 132,
                p: 2.25,
                borderRadius: 2,
                bgcolor: hexTillRgba(djup(kombo), 0.42),
                border: `1px solid ${hexTillRgba(ljus(kombo), 0.36)}`,
                boxShadow: `0 18px 40px -18px ${hexTillRgba(djup(kombo), 0.9)}`,
                backdropFilter: 'blur(8px)',
                backfaceVisibility: vänder ? 'hidden' : undefined
            }}
        >
            {children}
        </Box>
    );
}
